import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

export default function AdminSetPasswordModal({ user, onClose, onSuccess }) {
  const { token } = useAuth();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successTime, setSuccessTime] = useState(null);

  useEffect(() => {
    const fetchPasswordHistory = async () => {
      if (!user?.id || !token) return;
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/admin/users/${user.id}/password-history`,
          {
            headers: { 'Authorization': `Bearer ${token}` },
          }
        );
        const data = await response.json();
        if (response.ok && data.history) {
          setHistory(data.history);
        }
      } catch (err) {
        console.error('Failed to fetch password history:', err);
      }
    };
    fetchPasswordHistory();
  }, [user?.id, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!password || !confirmPassword) {
      setError('Both password fields are required');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/users/set-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          user_id: user.id,
          new_password: password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.error || 'Failed to set password');
      }

      // Show success modal
      setPassword('');
      setConfirmPassword('');
      setSuccessTime(new Date());
      setShowSuccess(true);
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
          <h2 className="text-xl font-semibold text-gray-900">Set Password</h2>
          <p className="text-sm text-gray-600 mt-1">{user.first_name} {user.last_name}</p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="p-3 bg-gray-50 border border-gray-200 rounded text-sm">
            {history.length > 0 ? (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600">Last reset:</p>
                  <p className="font-medium text-gray-900">
                    {new Date(history[0].created_at).toLocaleString('en-SG', { timeZone: 'Asia/Singapore' })}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowHistory(true)}
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                >
                  View history
                </button>
              </div>
            ) : (
              <p className="text-gray-600">No password resets</p>
            )}
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              Temporary Password
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2 pr-10 bg-gray-50 border border-gray-300 rounded text-gray-900 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                placeholder="••••••••"
                minLength="8"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-900 transition-colors"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
              Confirm Password
            </label>
            <div className="relative">
              <input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={`w-full px-4 py-2 pr-10 bg-gray-50 border rounded text-gray-900 text-sm focus:outline-none ${
                  confirmPassword && password === confirmPassword
                    ? 'border-green-500 focus:border-green-500 focus:ring-1 focus:ring-green-500'
                    : confirmPassword && password !== confirmPassword
                    ? 'border-red-500 focus:border-red-500 focus:ring-1 focus:ring-red-500'
                    : 'border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500'
                }`}
                placeholder="••••••••"
                minLength="8"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-900 transition-colors"
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
              >
                {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
            {confirmPassword && (
              <p className={`text-sm mt-1 ${password === confirmPassword ? 'text-green-600' : 'text-red-600'}`}>
                {password === confirmPassword ? '✓ Passwords match' : '✗ Passwords do not match'}
              </p>
            )}
          </div>

          <p className="text-xs text-gray-600">Minimum 8 characters</p>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !password || !confirmPassword || password !== confirmPassword || password.length < 8}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {submitting ? 'Setting...' : 'Set Password'}
            </button>
          </div>
        </form>
      </div>

      {showHistory && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Password Reset History</h3>
              <button
                onClick={() => setShowHistory(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
              >
                ×
              </button>
            </div>
            <div className="p-6 space-y-3">
              {history.length === 0 ? (
                <p className="text-gray-600 text-sm">No password resets yet</p>
              ) : (
                history.map((entry, idx) => (
                  <div key={idx} className="p-3 bg-gray-50 rounded border border-gray-200 text-sm">
                    <p className="font-medium text-gray-900">
                      {new Date(entry.created_at).toLocaleString('en-SG', { timeZone: 'Asia/Singapore' })}
                    </p>
                    <p className="text-gray-600 text-xs mt-1">
                      {entry.action === 'admin_set_password' ? 'Admin set password' : 'Forced reset'}
                    </p>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {showSuccess && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
                <span className="text-green-600 text-2xl">✓</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Password Set Successfully
              </h3>
              <p className="text-gray-600 text-sm mb-1">
                {user.first_name} {user.last_name}
              </p>
              <p className="text-gray-600 text-sm mb-1">
                {user.wellis_id || user.affiliate_code}
              </p>
              <p className="text-gray-600 text-sm mb-4">
                {user.email}
              </p>
              {successTime && (
                <div className="bg-gray-50 rounded p-3 mb-6">
                  <p className="text-gray-600 text-xs mb-1">Reset time (GMT+8):</p>
                  <p className="font-medium text-gray-900">
                    {successTime.toLocaleString('en-SG', { timeZone: 'Asia/Singapore' })}
                  </p>
                </div>
              )}
              <p className="text-gray-600 text-xs mb-6">
                Member will be prompted to change password on next login
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowSuccess(false);
                    onClose();
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded font-medium hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    setShowSuccess(false);
                    onSuccess();
                    onClose();
                  }}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded font-medium hover:bg-green-700 transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
